import React, { useEffect } from 'react';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Text } from '@mantine/core';
import { useNoteEditor } from '../../contexts/NoteEditorContext';
import { Extension } from '@tiptap/core';

// Custom extension to handle tab behavior for indentation
const TabExtension = Extension.create({
  name: 'tabHandler',
  
  addKeyboardShortcuts() {
    return {
      Tab: ({ editor }) => {
        // If we're in a list, indent the list item
        if (editor.isActive('listItem')) {
          return editor.commands.sinkListItem('listItem');
        }
        // Otherwise, insert 2 spaces (or could be a tab character)
        return editor.commands.insertContent('  ');
      },
      'Shift-Tab': ({ editor }) => {
        // If we're in a list, outdent the list item
        if (editor.isActive('listItem')) {
          return editor.commands.liftListItem('listItem');
        }
        // Otherwise, do nothing (could implement custom outdent logic here)
        return false;
      },
    };
  },
});

interface NoteRichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  onBlur?: (e: React.FocusEvent) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  isEditing: boolean;
  showToolbar?: boolean;
  compactMode?: boolean;
  noteElement?: HTMLElement;
  autoFocus?: boolean;
  editorRef?: React.MutableRefObject<any>;
  // Allow any TipTap Extension/Mark/Node; keep loose to support custom marks
  additionalExtensions?: any[];
}

export const NoteRichTextEditor: React.FC<NoteRichTextEditorProps> = ({
  content,
  onChange,
  onBlur,
  onKeyDown,
  placeholder = 'Start typing...',
  isEditing,
  showToolbar = true,
  compactMode = false,
  noteElement,
  autoFocus = true,
  editorRef,
  additionalExtensions = []
}) => {
  // Only use context in document mode (when not in compactMode)
  const noteEditorContext = !compactMode ? useNoteEditor() : undefined;
  
  // Validate additional extensions to prevent duplicates with StarterKit
  const validatedExtensions = React.useMemo(() => {
    const starterKitExtensionNames = new Set([
      'document', 'paragraph', 'text', 'bold', 'italic', 'strike', 
      'code', 'heading', 'bulletList', 'orderedList', 'listItem', 
      'codeBlock', 'blockquote', 'horizontalRule', 'hardBreak', 
      'dropcursor', 'gapcursor', 'history', 'link'
    ]);
    
    const filtered = additionalExtensions.filter((ext) => {
      const extName = ext.name?.toLowerCase();
      if (starterKitExtensionNames.has(extName)) {
        console.warn(`TipTap: Skipping extension "${extName}" - already included in StarterKit`);
        return false;
      }
      return true;
    });
    
    return filtered;
  }, [additionalExtensions]);
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Configure Link extension (included in StarterKit by default)
        link: {
          autolink: true,
          linkOnPaste: true,
          openOnClick: true,
          HTMLAttributes: {
            rel: 'noopener noreferrer nofollow',
            target: '_blank',
          },
        },
      }),
      TabExtension,
      ...validatedExtensions,
    ],
    content: content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    editable: isEditing,
    // Configure to be more minimal for note cards
    parseOptions: {
      preserveWhitespace: 'full'
    }
  });

  // Update editor content when prop changes
  useEffect(() => {
    if (editor && editor.getHTML() !== content) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  // Register editor on mount for document mode (when context is available)
  useEffect(() => {
    if (editor && noteEditorContext) {
      // Always register the editor when it's mounted in document mode
      noteEditorContext.setActiveEditor(editor);
      if (noteElement) {
        noteEditorContext.setActiveNoteElement(noteElement);
      }
    }
    // Also assign to ref if provided
    if (editor && editorRef) {
      editorRef.current = editor;
    }
  }, [editor, noteEditorContext, noteElement, editorRef]);

  // Handle editing state changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(isEditing);
      
      // Update context for document mode (when toolbar needs access)
      if (noteEditorContext) {
        noteEditorContext.setActiveEditor(editor);
        noteEditorContext.setIsEditingNote(isEditing);
        if (noteElement) {
          noteEditorContext.setActiveNoteElement(noteElement);
        }
      }
      
      // Focus editor when it becomes editable (only if autoFocus is enabled)
      if (isEditing && autoFocus) {
        setTimeout(() => {
          editor.commands.focus();
        }, 10);
      }
    }
  }, [isEditing, editor, noteEditorContext, noteElement, autoFocus]);
  
  // Cleanup editor registration on unmount
  useEffect(() => {
    return () => {
      if (noteEditorContext && editor) {
        // Only clear if this editor is currently the active one
        if (noteEditorContext.activeEditor === editor) {
          noteEditorContext.setActiveEditor(null);
          noteEditorContext.setIsEditingNote(false);
          noteEditorContext.setActiveNoteElement(null);
        }
      }
    };
  }, [noteEditorContext, editor]);

  const handleFocus = () => {
    // Register this editor when focused in document mode
    if (noteEditorContext && editor) {
      noteEditorContext.setActiveEditor(editor);
      if (noteElement) {
        noteEditorContext.setActiveNoteElement(noteElement);
      }
    }
  };

  if (!editor) {
    return (
      <Text
        size="sm"
        style={{
          color: 'var(--app-text-primary)',
          lineHeight: 1.4,
          cursor: 'text',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}
      >
        {content || placeholder}
      </Text>
    );
  }

  if (!isEditing) {
    // Read-only view with rendered HTML - match editor styles exactly
    return (
      <>
        <style>
          {`
            .note-content p {
              margin: 0 0 0.5em 0;
              color: var(--app-text-primary) !important;
            }
            .note-content p:last-child {
              margin-bottom: 0;
            }
            .note-content * {
              color: inherit !important;
            }
            .note-content blockquote {
              color: var(--app-text-primary) !important;
              background-color: color-mix(in srgb, var(--app-surface) 80%, var(--app-bg)) !important;
              border-left: 4px solid var(--app-border) !important;
              padding: 12px 16px !important;
              margin: 16px 0 !important;
              border-radius: 0 8px 8px 0 !important;
            }
          `}
        </style>
        <div
          className="note-content"
          style={{
            fontSize: compactMode ? '14px' : '16px',
            color: 'var(--app-text-primary)',
            lineHeight: 1.4,
            cursor: 'text',
            wordBreak: 'break-word',
            minHeight: compactMode ? '40px' : '60px',
            margin: 0,
            padding: 0
          }}
          onClick={handleFocus}
          dangerouslySetInnerHTML={{ 
            __html: content || `<p style="color: var(--app-text-muted); margin: 0;">${placeholder}</p>` 
          }}
        />
      </>
    );
  }

  return (
    <>
      <style>
        {`
          /* Ensure ProseMirror content uses theme colors */
          .ProseMirror {
            color: var(--app-text-primary) !important;
          }
          .ProseMirror p {
            color: var(--app-text-primary) !important;
          }
          .ProseMirror h1, .ProseMirror h2, .ProseMirror h3, .ProseMirror h4, .ProseMirror h5, .ProseMirror h6 {
            color: var(--app-text-primary) !important;
          }
          .ProseMirror ul, .ProseMirror ol, .ProseMirror li {
            color: var(--app-text-primary) !important;
          }
          .ProseMirror strong, .ProseMirror em, .ProseMirror code {
            color: var(--app-text-primary) !important;
          }
          .ProseMirror blockquote {
            color: var(--app-text-primary) !important;
            background-color: color-mix(in srgb, var(--app-surface) 80%, var(--app-bg)) !important;
            border-left-color: var(--app-border) !important;
            padding: 12px 16px !important;
            margin: 16px 0 !important;
            border-radius: 0 8px 8px 0 !important;
          }
        `}
      </style>
      <RichTextEditor
        editor={editor}
      styles={{
        root: {
          border: 'none',
          backgroundColor: 'transparent'
        },
        content: {
          backgroundColor: 'transparent',
          border: 'none',
          padding: 0,
          margin: 0,
          fontSize: compactMode ? '14px' : '16px',
          color: 'var(--app-text-primary)',
          minHeight: compactMode ? '40px' : '60px',
          lineHeight: 1.4,
          '& p': {
            margin: '0 0 0.5em 0'
          },
          '& p:last-child': {
            marginBottom: 0
          }
        },
        toolbar: {
          backgroundColor: 'var(--app-surface)',
          border: '1px solid var(--app-border)',
          borderRadius: '8px',
          padding: compactMode ? '2px' : '4px',
          marginBottom: '8px',
          display: showToolbar ? 'flex' : 'none'
        }
      }}
      onFocus={handleFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    >
      {showToolbar && (
        <RichTextEditor.Toolbar>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Strikethrough />
          </RichTextEditor.ControlsGroup>

          {!compactMode && (
            <>
              <RichTextEditor.ControlsGroup>
                <RichTextEditor.H1 />
                <RichTextEditor.H2 />
                <RichTextEditor.H3 />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.BulletList />
                <RichTextEditor.OrderedList />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Code />
                <RichTextEditor.CodeBlock />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Blockquote />
                <RichTextEditor.Hr />
              </RichTextEditor.ControlsGroup>

              <RichTextEditor.ControlsGroup>
                <RichTextEditor.Undo />
                <RichTextEditor.Redo />
              </RichTextEditor.ControlsGroup>
            </>
          )}
        </RichTextEditor.Toolbar>
      )}

      <RichTextEditor.Content />
    </RichTextEditor>
    </>
  );
};

export default NoteRichTextEditor;